import { Link } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/shared/components/error-state';
import { StatusBadge, statusVariant } from '@/shared/components/status-badge';
import { formatCurrency, formatDateTime } from '@/shared/lib/format';

import { useCustomerComplaints, useCustomerRefunds } from '../api/customers.queries';

/** Complaints on the customer's orders (shown with complaints.manage). */
export function CustomerComplaintsCard({ customerId, zone }: { customerId: number; zone: string }) {
  const complaints = useCustomerComplaints(customerId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Complaints</CardTitle>
      </CardHeader>
      <CardContent className="text-xs">
        {complaints.isLoading ? (
          <Skeleton className="h-16" />
        ) : complaints.error ? (
          <ErrorState error={complaints.error} onRetry={() => void complaints.refetch()} />
        ) : !complaints.data?.length ? (
          <p className="text-muted-foreground">No complaints from this customer.</p>
        ) : (
          <ul className="space-y-1.5" aria-label="Customer complaints">
            {complaints.data.map((complaint) => (
              <li key={complaint.id} className="flex flex-wrap justify-between gap-2">
                <Link to={`/complaints/${complaint.id}`} className="text-primary hover:underline">
                  #{complaint.id} {complaint.complaintType}
                </Link>
                <span className="text-muted-foreground flex items-center gap-1.5">
                  <StatusBadge variant={statusVariant(complaint.status)}>
                    {complaint.status}
                  </StatusBadge>
                  {complaint.assigneeName ?? 'Unassigned'} ·{' '}
                  {formatDateTime(complaint.complaintDate, zone)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Refunds recorded for the customer, with payout state (shown with refunds.request). */
export function CustomerRefundsCard({ customerId, zone }: { customerId: number; zone: string }) {
  const refunds = useCustomerRefunds(customerId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Refunds</CardTitle>
      </CardHeader>
      <CardContent className="text-xs">
        {refunds.isLoading ? (
          <Skeleton className="h-16" />
        ) : refunds.error ? (
          <ErrorState error={refunds.error} onRetry={() => void refunds.refetch()} />
        ) : !refunds.data?.length ? (
          <p className="text-muted-foreground">No refunds for this customer.</p>
        ) : (
          <ul className="space-y-1.5" aria-label="Customer refunds">
            {refunds.data.map((refund) => (
              <li key={refund.id} className="flex flex-wrap justify-between gap-2">
                <span>
                  {formatCurrency(refund.refundAmount, refund.currencyCode ?? 'USD')}
                  {refund.orderId !== null && (
                    <>
                      {' · '}
                      <Link
                        to={`/orders/${refund.orderId}`}
                        className="text-primary hover:underline"
                      >
                        order #{refund.orderId}
                      </Link>
                    </>
                  )}
                </span>
                <span className="text-muted-foreground flex items-center gap-1.5">
                  {refund.paidAt ? (
                    <StatusBadge variant="success">Paid out</StatusBadge>
                  ) : (
                    <StatusBadge variant="warning">Payout not recorded</StatusBadge>
                  )}
                  {formatDateTime(refund.creationDate, zone)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
